import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Paper,
  Container,
  Grid,
  Button,
  Step,
  Stepper,
  StepButton,
  StepLabel,
  Typography,
  Divider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ResearcherInfo from './ResearcherInfo';
import FilesList from './FilesList';
import ResidualDisclosure from './ResidualDisclosure';
import AdditionalInfo from './AdditionalInfo';
import RequestToolBar from './RequestToolBar';
import AppbarAssignees from './AppBarAssignees';
import FloatingSupportButton from '../CommonComponents/Support';
import Header from '../CommonComponents/Header';
import Footer from '../CommonComponents/Footer';
import CutCopyPasteAlert from '../CommonComponents/CutCopyPasteAlert';
import {DialogSaveBeforeLeaving} from '../CommonComponents/DialogBox';
import BypassBlocks from '../CommonComponents/BypassBlocks';
import Snackbar from '../CommonComponents/Snackbar';

import _ from "lodash";
import {useHistory} from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {RequestStatusId, UserRole, RequestActionId} from '../../ApplicationConfig/constants';
import {Auth} from '../../Api/auth';
import {UserInfoContext} from '../../StateObjects/UserInfoContext';

import { useMsal } from "@azure/msal-react";

const PREFIX = 'index';

const classes = {
  container: `${PREFIX}-container`,
  main: `${PREFIX}-main`,
  paper: `${PREFIX}-paper`,
  maxWidth640: `${PREFIX}-maxWidth640`,
  title: `${PREFIX}-title`,
  stepperContainer: `${PREFIX}-stepperContainer`,
  stepperNextBtn: `${PREFIX}-stepperNextBtn`,
  stepperBackBtn: `${PREFIX}-stepperBackBtn`,
  stepperText: `${PREFIX}-stepperText`,
  backdrop: `${PREFIX}-backdrop`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
  },

  [`& .${classes.main}`]: {
    background: theme.palette.grey[100],
    paddingBottom: theme.spacing(6),
    flex: 1,
  },

  [`& .${classes.paper}`]: {
    maxWidth: '1280px',
    margin: 'auto',
    boxSizing: 'border-box',
    padding: theme.spacing(6),
    marginTop: theme.spacing(3),
  },

  [`& .${classes.maxWidth640}`]: {
    maxWidth: '640px',
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.stepperContainer}`]: {
    'display': 'flex',
    '& .MuiStepper-root': {
      flexGrow: 1,
      padding: 0,
    },
  },

  [`& .${classes.stepperNextBtn}`]: {
    marginLeft: theme.spacing(6),
  },

  [`& .${classes.stepperBackBtn}`]: {
    marginRight: theme.spacing(6),
  },

  [`& .${classes.stepperText}`]: {
    margin: 0,
    textAlign: 'left',
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  }
}));

export default function VettingRequest(props){


  const history = useHistory();
  const { t,i18n } = useTranslation();
  const {instance}=useMsal();

  const {userInfo} = React.useContext(UserInfoContext);
  const [formData, setFormData] = React.useState(props.request.data);
  const [lastSaveFormData, setLastSaveFormData] = React.useState(_.cloneDeep(props.request.data));
  const [dialogUnsaved, setDialogUnsaved] = React.useState({
    open: false,
    pathname: null,
    isExternal: false
  });
  const [backdropOpen, setBackdropOpen] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState({});
  const [fileFolders, setFileFolders] = React.useState({
    filesObject: {}, 
    foldersList: [],
    filesList: []
  });
  const [state, setState] = React.useState({
    activeStep: 0
  });
  const [snackbarState, setSnackbarState] = React.useState({
    severity: "success",
    message: "",
    snackbarLeave: false,
    snackbarSupport: false,
    snackbarUpdate: false,
    snackbarApproved: false,
    snackbarDenied: false,
    snackbarWithdraw: false,
    snackbarReactivate: false,
    snackbarDelete: false,
    snackbarAssignPrimary: false,
    snackbarAssignSupport: false,
    snackbarUnassign: false,
    snackbarAssignSupportNoLead: false,
    open: false,
  });

  const steps = [
    "researcherInformation",
    "filesList",
    "residualDisclosure",
    "additionalInformation"
  ];
  const refStep0 = React.useRef(null);
  const refStep1 = React.useRef(null);
  const refStep2 = React.useRef(null);
  const refStep3 = React.useRef(null);
  const mainRef = React.createRef();
  const footerRef = React.createRef();

  React.useEffect(()=>{
    document.title = t("requestForm.title", {request: props.request.identifier});
  }, []);

  const getStepRef=(step)=>{
    switch(step){
      case 0:
        return refStep0;
      case 1:
        return refStep1;
      case 2:
        return refStep2;
      case 3:
        return refStep3;
      default:
        return null;
    }
  }

  const handleNext = () => {
    window.scrollTo(0, 0);
    const prevActiveStep = state.activeStep;
    setState({...state, activeStep: prevActiveStep + 1});
    getStepRef(prevActiveStep + 1).current.focus();
  };

  const handleBack = () => {
    window.scrollTo(0, 0);
    const prevActiveStep = state.activeStep;
    setState({...state, activeStep: prevActiveStep - 1});
    getStepRef(prevActiveStep - 1).current.focus();
  };

  const handleStep = (step) => {
    window.scrollTo(0, 0);
    setState({...state, activeStep: step});
  };

  const handleCloseSnackbar=(snack)=>{
    setSnackbarState({...snackbarState,
      message: "",
      [snack]: false
    });
  }

  const deletedFileErrorMessage = "applicationErrorMessage.request.deletedFile";
  const cleanInvalidFiles=(invalidFiles)=>{
    let filesListErrs = {};
    let residualDisclosureErrs = {};
    for(let i = 0; i < formData.outputFiles.length; i++){
      let file = formData.outputFiles[i];
      //if the output file doesnt exist
      if(file.outputFile !== "" && invalidFiles.includes(file.outputFile)){
        filesListErrs[`outputFiles.${i}.outputFile`]=deletedFileErrorMessage;
      }
      //for each output supporting file
      Object.keys(file.supportingFiles).forEach((supportType)=>{
        file.supportingFiles[supportType].forEach((file, j)=>{
          if(file.fileName!=="" && invalidFiles.includes(file.fileName)){
            filesListErrs[`outputFiles.${i}.supportingFiles.${supportType}.${j}.fileName`]=deletedFileErrorMessage;
          }
        });
      });
    }
    //for each general supporting file
    Object.keys(formData.supportingFiles).forEach((fileType)=>{
      switch(fileType){
        case "syntaxFile":
        case "variableListFile":
        case "other":
          formData.supportingFiles[fileType].forEach((file, i)=>{
            if(file.fileName !== "" && invalidFiles.includes(file.fileName)){
              filesListErrs[`supportingFiles.${fileType}.${i}.fileName`]=deletedFileErrorMessage;
            }
          });
          break;
        case "previousRelease_residualTablesFile":
        case "previousRelease_bothSetsFile":
          formData.supportingFiles[fileType].forEach((file, i)=>{
            if(file.fileName !== "" && invalidFiles.includes(file.fileName)){
              residualDisclosureErrs[`supportingFiles.${fileType}.${i}.fileName`]=deletedFileErrorMessage;
            }
          });
          break;
        default:
          break;
      }
    });

    let errs = {};
    if(Object.keys(filesListErrs).length!==0){
      errs["filesList"]=filesListErrs;
    }
    if(Object.keys(residualDisclosureErrs).length!==0){
      errs["residualDisclosure"]=residualDisclosureErrs;
    }
    if(Object.keys(errs).length!==0){
      setFormErrors(errs);
    }
  };

  const handleUnsavedDialogClose=()=>{
    setDialogUnsaved({
      open: false,
      pathname: null,
      isExternal: false
    });
  }

  const handleSupport=(contentToPost)=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_id: props.request.id,steps:contentToPost}),
    };  
    
    Auth.authorizedFetch("/api/requestHelp",requestOptions,instance)
    .then(res=>{
      if(res.ok){
        setSnackbarState({...snackbarState,
          severity: "success",
          message: t('notifications.vettingRequestHelpRequested'),
          snackbarSupport: true
        });
      }else if(res.status===403){
        setSnackbarState({...snackbarState,
          severity: "warning",
          message: t('applicationErrorMessage.request.helpAlreadySent'),
          snackbarSupport: true
        });
      }else{
        setSnackbarState({...snackbarState,
          severity: "error",
          message: t("applicationErrorMessage.generic.dataError"),
          snackbarSupport: true
        });
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState,
        severity: "error",
        message: t("applicationErrorMessage.generic.dataError"),
        snackbarSupport: true
      });
    })
  };

  const handleSave = ()=>{
    setBackdropOpen(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        request_id: props.request.id,
        formData:formData
      })
    };

    Auth.authorizedFetch("/api/saveRequest", requestOptions,instance)
    .then(res=>res.json())
    .then(response=>{
      if(response.statusCode===200){
        if(dialogUnsaved.pathname){
          if(dialogUnsaved.isExternal){
            window.location.href=dialogUnsaved.pathname;
          }else{
            history.push(dialogUnsaved.pathname, {
              snackbar: {severity: "success", message: t("requestForm.successSave")}
            });
          }
        }else{
          setSnackbarState({...snackbarState,
            severity: "success",
            message: t("requestForm.successSave"),
            open: true
          });
          setLastSaveFormData(_.cloneDeep(formData));
        }
      }else{
        if(Object.prototype.hasOwnProperty.call(response, "customMessageKey")){
          if(dialogUnsaved.pathname){
            setSnackbarState({...snackbarState,
              snackbarLeave: true,
              severity: "error",
              message: t("applicationErrorMessage.request." + response.customMessageKey)
            });
          }else{
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.request." + response.customMessageKey),
              open: true
            });
          }
        }else{
          if(dialogUnsaved.pathname){
            setSnackbarState({...snackbarState,
              snackbarLeave: true,
              severity: "error",
              message: t("applicationErrorMessage.request.submitError")
            });
          }else{
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.request.submitError"),
              open: true
            });
          }
        }
      }
    })
    .catch(()=>{
      if(dialogUnsaved.pathname){
        setSnackbarState({...snackbarState,
          snackbarLeave: true,
          severity: "error",
          message: t("applicationErrorMessage.request.submitError")
        });
      }else{
        setSnackbarState({...snackbarState,
          severity: "error",
          message: t("applicationErrorMessage.request.submitError"),
          open: true
        });
      }
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  };

  const handleSubmit=()=> {
    setBackdropOpen(true);
    let requestOptions = {
      method:"POST", 
      headers:{'Content-type':'application/json'},
      body: JSON.stringify({
        request_id:props.request.id,
        formData:formData
      })
    }
    Auth.authorizedFetch("/api/submitRequest", requestOptions,instance)
    .then(res=> res.json())
    .then(result=>{
      if(result.statusCode===200){
        history.push("/"+i18n.language+"/home", {
          snackbar: {severity: "success", message: t("notifications.vettingRequestSubmitted")}
        });
      }else{
        setLastSaveFormData(_.cloneDeep(formData));
        if(Object.prototype.hasOwnProperty.call(result.data, "formErrors")){
          setFormErrors(result.data.formErrors);
          let errorCount = 0;
          Object.keys(result.data.formErrors).forEach(k=>{
            errorCount+=Object.keys(result.data.formErrors[k]).length;
          });
          if(errorCount > 99){
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.request.intenseFormErrors"),
              open: true
            });
          }else{
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.request.formErrors", {count: errorCount}),
              open: true
            });
          }
          if(Object.prototype.hasOwnProperty.call(result.data.formErrors, "researcherInformation")){
            handleStep(0);
            getStepRef(0).current.focus();
          }else if(Object.prototype.hasOwnProperty.call(result.data.formErrors, "filesList")){
            handleStep(1);
            getStepRef(1).current.focus();
          }else if(Object.prototype.hasOwnProperty.call(result.data.formErrors, "residualDisclosure")){
            handleStep(2);
            getStepRef(2).current.focus();
          }
        }else if(Object.prototype.hasOwnProperty.call(result.data, "invalidFiles")){
          cleanInvalidFiles(result.data.invalidFiles);
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.request.invalidFiles"),
            open: true
          });
        }else if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.request." + result.customMessageKey),
            open: true
          });
        }else if(Object.prototype.hasOwnProperty.call(result.data, "failedFiles")){
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.request.copyError"),
            open: true
          });
        }else{
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.request.submitError"),
            open: true
          });
        }
        setBackdropOpen(false);
      }
    })
    .catch(()=> {
      setSnackbarState({...snackbarState,
        severity: "error",
        message: t("applicationErrorMessage.request.submitError"),
        open: true
      });
      setBackdropOpen(false);
    })
  };

  const handleRequestUpdate=()=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_id: props.request.id, 
        formData:formData
      })
    };
    
    Auth.authorizedFetch("/api/requestUpdate", requestOptions,instance)
    .then(res=>res.json())
    .then(result=>{
      if(result.statusCode===200){
        history.push("/"+i18n.language+"/home",{
          snackbar: {severity: "success", message: t('notifications.vettingRequestUpdateRequested')}
        });
      }else{
        if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!="" && result.customMessageKey!="" && i18n.exists("applicationErrorMessage.generic." + result.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarUpdate: true, severity: "error", message: t("applicationErrorMessage.generic." + result.customMessageKey)});
        }else if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!="" && result.customMessageKey!="" && i18n.exists("applicationErrorMessage.request." + result.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarUpdate: true, severity: "error", message: t("applicationErrorMessage.request." + result.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarUpdate: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarUpdate: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    });
  };

  const handleApproveRequest=(dialogData)=>{
    setBackdropOpen(true);
    let requestBody = {request_id:props.request.id, 
      billable_hours:dialogData.billableHours,
      billable_minutes:dialogData.billableMinutes,
      formData:formData
    };
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    };
    
    Auth.authorizedFetch("/api/approveRequest", requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      if(data.statusCode===200){
        history.push("/"+i18n.language+"/home", {
          snackbar: {severity: "success", message: t('notifications.vettingRequestApproved', {request: props.request.identifier})}
        });
      }else{
        if(Object.prototype.hasOwnProperty.call(data.data, "formErrors")){
          setFormErrors(data.data.formErrors);
        }else if(Object.prototype.hasOwnProperty.call(data, "customMessageKey") && i18n.exists("applicationErrorMessage.generic." + data.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarApproved: true, severity: "error", message: t("applicationErrorMessage.generic." + data.customMessageKey)});
        }else if(Object.prototype.hasOwnProperty.call(data, "customMessageKey") && i18n.exists("applicationErrorMessage.request." + data.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarApproved: true, severity: "error", message: t("applicationErrorMessage.request." + data.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarApproved: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
        setBackdropOpen(false);
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarApproved: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
      setBackdropOpen(false);
    });
  };

  const handleDenyRequest=(dialogData)=>{
    setBackdropOpen(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({request_id:props.request.id,
        reason_comment:dialogData.reasonComment,
        reason_id:dialogData.reasonId,
        billable_hours:dialogData.billableHours,
        billable_minutes:dialogData.billableMinutes,
        formData:formData
      })
    };
    
    Auth.authorizedFetch("/api/denyRequest", requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      if(data.statusCode===200){
        history.push("/"+i18n.language+"/home", {
          snackbar: {severity: "success", message: t('notifications.vettingRequestDenied', {request: props.request.identifier})}
        });
      }else{
        if(Object.prototype.hasOwnProperty.call(data.data, "formErrors")){
          setFormErrors(data.data.formErrors);
        }else if(Object.prototype.hasOwnProperty.call(data, "customMessageKey") && i18n.exists("applicationErrorMessage.generic." + data.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarDenied: true, severity: "error", message: t("applicationErrorMessage.generic." + data.customMessageKey)});
        }else if(Object.prototype.hasOwnProperty.call(data, "customMessageKey") && i18n.exists("applicationErrorMessage.request." + data.customMessageKey)){
          setSnackbarState({...snackbarState, snackbarDenied: true, severity: "error", message: t("applicationErrorMessage.request." + data.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarDenied: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
        setBackdropOpen(false);
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarDenied: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
      setBackdropOpen(false);
    });
  };

  const handleAssignMe=(dialogData, analystRole)=>{
    let url="";
    let message="";
    let snack="";
    if(analystRole==="lead"){
      url="/api/assignAsPrimaryAnalyst";
      message=t('notifications.vettingRequestAssignToMe');
      snack="snackbarAssignPrimary";
    }else if(analystRole==="support"){
      url="/api/assignAsSupportAnalyst";
      message=t('notifications.vettingRequestAssignAsSupport');
      snack="snackbarAssignSupport";
    }
    if(props.request.primaryAnalyst.account === userInfo.id && analystRole==="support"){
      snack="snackbarAssignSupportNoLead";
    }

    let requestBody={request_id: props.request.id};
    if(dialogData){
      requestBody={...requestBody, phone_number:dialogData};
    }
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    };
    Auth.authorizedFetch(url,requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      if(data.statusCode===200){
        setSnackbarState({...snackbarState, [snack]: true, severity: "success", message: message});
        props.handleRefreshForm();
      }else{
        if(Object.prototype.hasOwnProperty.call(data, "customMessageKey")){
          setSnackbarState({...snackbarState, [snack]: true, severity: "error", message: t("applicationErrorMessage.request." + data.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, [snack]: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, [snack]: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    });
  };

  const handleUnassign=()=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_id:props.request.id,
        formData:formData
      })
    };
    
    Auth.authorizedFetch("/api/unassignMyself",requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      if(data.statusCode===200){
        setSnackbarState({...snackbarState, snackbarUnassign: true, severity: "success", message: t('notifications.vettingRequestUnassigned')});
        setLastSaveFormData(_.cloneDeep(formData));
        props.handleRefreshForm();
      }else{
        if(Object.prototype.hasOwnProperty.call(data, "customMessageKey")){
          setSnackbarState({...snackbarState, snackbarUnassign: true, severity: "error", message: t("applicationErrorMessage.request." + data.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarUnassign: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarUnassign: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    });
  };

  const handleValidateRequest=(fn)=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_id:props.request.id,
        formData:formData
      })
    };
    
    Auth.authorizedFetch("/api/validateRequest", requestOptions,instance)
    .then(res=>res.json())
    .then(data=>{
      if(data.statusCode===200){
        setLastSaveFormData(_.cloneDeep(formData));
        fn();
      }else{
        if(Object.prototype.hasOwnProperty.call(data.data, "formErrors")){
          setFormErrors(data.data.formErrors);
        }else if(Object.prototype.hasOwnProperty.call(data, "customMessageKey") && i18n.exists("applicationErrorMessage.request." + data.customMessageKey)){
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.request." + data.customMessageKey),
            open: true
          });
        }else{
          setSnackbarState({...snackbarState,
            severity: "error",
            message: t("applicationErrorMessage.generic.dataError"),
            open: true
          });
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState,
        severity: "error",
        message: t("applicationErrorMessage.generic.dataError"),
        open: true
      });
    });
  };

  const handleDelete=()=>{
    Auth.authorizedFetch("/api/updateRow", {method:"POST", body: JSON.stringify({request_id:props.request.id, new_status_id: RequestActionId.Delete}), headers:{'Content-type':'application/json'}},instance)
    .then(res=>res.json())
    .then(result=>{
      if(result.statusCode===200){
        history.push("/"+i18n.language+"/home", {
          snackbar: {severity: "success", message: t("notifications.vettingRequestDeleted")}
        });
      }else{
        if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
          setSnackbarState({...snackbarState, snackbarDelete: true, severity: "error", message: t("applicationErrorMessage.request." + result.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarDelete: true, severity: "error", message: t("applicationErrorMessage.request.requestErrorDeleting")});
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarDelete: true, severity: "error", message: t("applicationErrorMessage.request.requestErrorDeleting")});
    })
  }

  const handleWithdraw=(data)=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_id: props.request.id,
        new_status_id: RequestStatusId.Withdrawn,
        status_reason_id: data.reason_id
      })
    };
    Auth.authorizedFetch("/api/updateRow", requestOptions,instance)
    .then(res=>res.json())
    .then(result=>{
      if(result.statusCode===200){
        setSnackbarState({...snackbarState, snackbarWithdraw: true, severity: "success", message: t('notifications.vettingRequestWithdrawn')});
        props.handleRefreshForm();
      }else{
        if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
          setSnackbarState({...snackbarState, snackbarWithdraw: true, severity: "error", message: t("applicationErrorMessage.request." + result.customMessageKey)});
        }else{
          setSnackbarState({...snackbarState, snackbarWithdraw: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
        }
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarWithdraw: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    })
  }

  const handleReactivate=(data)=>{
    let requestOptions={};
    let requestUrl="";
    if(userInfo.role===UserRole.Researcher){
      requestUrl="/api/updateRow";
      requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          request_id: props.request.id,
          new_status_id: RequestStatusId.Draft
        })
      };
    }else{
      requestUrl="/api/reactivateRequest";
      requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          request_id: props.request.id,
          phone_number:data
        })
      };
    }
    Auth.authorizedFetch(requestUrl, requestOptions,instance)
    .then(res=>res.json())
    .then(result=>{
      if(result.statusCode==200){
        props.handleRefreshForm();
        setSnackbarState({...snackbarState, snackbarReactivate: true, severity: "success", message: t('notifications.vettingRequestReopened')});
      }else if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
        setSnackbarState({...snackbarState, snackbarReactivate: true, severity: "error", message: t("applicationErrorMessage.request." + result.customMessageKey)});
      }else{
        setSnackbarState({...snackbarState, snackbarReactivate: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState, snackbarReactivate: true, severity: "error", message: t("applicationErrorMessage.generic.dataError")});
    })
  }

  const handleStartReview=()=>{
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_id: props.request.id,
        new_status_id: RequestStatusId.DisclosureAnalysis
      })
    };
    Auth.authorizedFetch("/api/updateRequestStatus",requestOptions,instance)
    .then(res=>res.json())
    .then((result)=>{
      if(result.statusCode==200){
        setSnackbarState({...snackbarState,
          severity: "success",
          message: t('notifications.vettingRequestStartReview'),
          open: true
        });
        props.handleRefreshForm();
      }else if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
        setSnackbarState({...snackbarState,
          severity: "error",
          message: t("applicationErrorMessage.request." + result.customMessageKey),
          open: true
        });
      }else{
        setSnackbarState({...snackbarState,
          severity: "error",
          message: t("applicationErrorMessage.generic.dataError"),
          open: true
        });
      }
    })
    .catch(()=>{
      setSnackbarState({...snackbarState,
        severity: "error",
        message: t("applicationErrorMessage.generic.dataError"),
        open: true
      });
    })
  }

  const getFoldersList=(filesData, path, depth)=>{
    let list = [];
    if(depth<=0){
      return list;
    }
    Object.keys(filesData).map(k=>{
      let newPath = path+k;
      list.push(newPath);
      list = list.concat(getFoldersList(filesData[k]["sub_dirs"], newPath+"/", depth-1));
    });
    return list;
  }

  const getSubDirsFiles=(folder, path)=>{
    let files = [];
    Object.keys(folder["sub_dirs"]).map(k=>{
      files = files.concat(getSubDirsFiles(folder["sub_dirs"][k], path+k+"/"));
    });
    files = files.concat(folder["files"].map(f=>{return path+f;}));
    return files;
  }

  const getFilesList=(folder, filesData)=>{
    let files = [];
    if(folder !== ""){
      let f = _.get(filesData, folder.replace(/\//g, ".sub_dirs."), {});
      if(Object.keys(f).length !== 0){
        files = getSubDirsFiles(f, "");
      }
    }
    return files;
  }

  const updateFilesList=(folder, filesData)=>{
    let files = getFilesList(folder, filesData);
    setFileFolders({
      ...fileFolders, 
      filesList: files
    });
  }

  const validateFormFiles=(files)=>{
    let filesListErrs = {};
    let residualDisclosureErrs = {};
    //for each output file
    for(let i = 0; i < formData.outputFiles.length; i++){
      let file = formData.outputFiles[i];
      //if the output file doesnt exist
      if(file.outputFile !== "" && !files.includes(file.outputFile)){
        filesListErrs[`outputFiles.${i}.outputFile`]=deletedFileErrorMessage;
      }
      //for each output supporting file
      Object.keys(file.supportingFiles).forEach((supportType)=>{
        file.supportingFiles[supportType].forEach((file, j)=>{
          if(file.fileName!=="" && !files.includes(file.fileName)){
            filesListErrs[`outputFiles.${i}.supportingFiles.${supportType}.${j}.fileName`]=deletedFileErrorMessage;
          }
        });
      });
    }

    //for each general supporting file
    Object.keys(formData.supportingFiles).forEach((fileType)=>{
      switch(fileType){
        case "syntaxFile":
        case "variableListFile":
          formData.supportingFiles[fileType].forEach((file, i)=>{
            if(file.fileName !== "" && !files.includes(file.fileName)){
              filesListErrs[`supportingFiles.${fileType}.${i}.fileName`]=deletedFileErrorMessage;
            }
          });
          break;
        case "subsetVariables":
        case "previousRelease":
          formData.supportingFiles[fileType].forEach((file, i)=>{
            if(file.fileName !== "" && !files.includes(file.fileName)){
              residualDisclosureErrs[`supportingFiles.${fileType}.${i}.fileName`]=deletedFileErrorMessage;
            }
          });
          break;
      }
    });

    let errs = {};
    if(Object.keys(filesListErrs).length!==0){
      errs["filesList"]=filesListErrs;
    }
    if(Object.keys(residualDisclosureErrs).length!==0){
      errs["residualDisclosure"]=residualDisclosureErrs;
    }
    if(Object.keys(errs).length!==0){
      setFormErrors(errs);
    }
  };

  React.useEffect(()=>{
    if(!props.readOnly && !props.analystEditFlag){
      setBackdropOpen(true);
      Auth.authorizedFetch("/api/getStorageAccountFolders/"+props.request.projectId,{},instance)
      .then(res => res.json())
      .then((result) => {
        if(result.statusCode===200){
          let foldersList = getFoldersList(result.data, "", 2);

          let rootIndex=foldersList.indexOf("Vetting Requests-Demandes de contrôle");
          if(rootIndex>=0){
            foldersList.splice(rootIndex, 1);
          }

          if(!props.readOnly && 
            !props.analystEditFlag && 
            Object.keys(foldersList).length !== 0 && 
            formData.outputFilesFolder !== "" && 
            !foldersList.includes(formData.outputFilesFolder)
          ){
            history.push("/"+i18n.language+"/home", {
              snackbar: {severity: "error", message: t("requestForm.errorWithRequest")}
            });
          }else{
            let files=getFilesList(formData.outputFilesFolder, result.data);
            validateFormFiles(files);
            setFileFolders({
              filesObject: result.data,
              foldersList: foldersList,
              filesList: files
            });
            setBackdropOpen(false);
          }
        }else{
          if(Object.prototype.hasOwnProperty.call(result, "customMessageKey") && result.customMessageKey!=""){
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.request." + result.customMessageKey),
              open: true
            });
          }else{
            setSnackbarState({...snackbarState,
              severity: "error",
              message: t("applicationErrorMessage.generic.dataError"),
              open: true
            });
          }
          setBackdropOpen(false);
        }
      })
      .catch((error)=>{
        setSnackbarState({...snackbarState,
          severity: "error",
          message: t("applicationErrorMessage.generic.dataError"),
          open: true
        });
        setBackdropOpen(false);
      });
    }
  },[props.readOnly, props.analystEditFlag]);

  const handleSaveBeforeLeave=(href, isExternal)=>{
    if(!_.isEqual(formData, lastSaveFormData)){
      handleCloseSnackbar("snackbarLeave");

      setDialogUnsaved({
        open: true,
        pathname: href,
        isExternal: isExternal
      });
    }else{
      if(isExternal){
        window.location.href=href;
      }else{
        history.push(href);
      }
    }
  }

  let isAnalyst = userInfo.role === UserRole.Analyst;

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ResearcherInfo 
            formData={formData} 
            setFormData={setFormData}
            errors={formErrors} 
            setErrors={setFormErrors}
            readOnly={props.readOnly}
            request={props.request}
            analystEditFlag={props.analystEditFlag}
            fileFolders={fileFolders}
            updateFilesList={updateFilesList} 
          />
        );
      case 1:
        return (
          <FilesList 
            formData={formData} 
            setFormData={setFormData} 
            errors={formErrors}
            setErrors={setFormErrors}
            readOnly={props.readOnly}
            analystEditFlag={props.analystEditFlag}
            isAnalyst={isAnalyst}
            fileFolders={fileFolders}
            requestStatus={props.request.status.id}
          />
        );
      case 2:
        return (
          <ResidualDisclosure 
            formData={formData} 
            setFormData={setFormData} 
            errors={formErrors}
            setErrors={setFormErrors}
            filesList={fileFolders.filesList} 
            readOnly={props.readOnly}
            analystEditFlag={props.analystEditFlag}
          />
        );
      case 3:
        return (
          <AdditionalInfo 
            formData={formData} 
            setFormData={setFormData} 
            readOnly={props.readOnly}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Root className={classes.container}>
      <BypassBlocks ref={{main: mainRef, footer: footerRef}} />
      <Header 
        handleSaveBeforeLeave={handleSaveBeforeLeave}
        source="form"
      >
        <RequestToolBar 
          handleSubmit={handleSubmit} 
          handleRequestUpdate={handleRequestUpdate}
          handleApproveRequest={handleApproveRequest} 
          handleDenyRequest={handleDenyRequest} 
          handleSave={handleSave}
          handleValidateRequest={handleValidateRequest}
          handleAssignMe={handleAssignMe}
          handleUnassign={handleUnassign}
          handleSaveBeforeLeave={handleSaveBeforeLeave}
          handleDelete={handleDelete}
          handleWithdraw={handleWithdraw}
          handleReactivate={handleReactivate}
          handleStartReview={handleStartReview}
          isRequester={props.request.researcher.account === userInfo.id}
          isLeadAnalyst={props.request.primaryAnalyst.account === userInfo.id}
          isSupportAnalyst={props.request.supportAnalysts.find(a=>a.account===userInfo.id) ? true : false}
          role={userInfo.role}
          readOnly={props.readOnly}
          request={props.request}
          handleRefreshForm={props.handleRefreshForm}
          setBackdropOpen={setBackdropOpen}
          snackbarState={snackbarState}
          handleCloseSnackbar={handleCloseSnackbar}
        />
      </Header>
      <main className={classes.main} ref={mainRef} tabIndex="-1">
        <Container maxWidth={false}>
          <Paper variant="outlined" className={classes.paper}>
            <Grid container alignItems="center">
              <AppbarAssignees
                request={props.request} 
                requestName={formData.requestName}
                handleRefreshForm={props.handleRefreshForm}
                userInfo={userInfo}
                setBackdropOpen={setBackdropOpen}
              />
            </Grid>
            <Divider sx={{mt: 3, mb: 3}} />
            <div className={classes.stepperContainer}>
              {state.activeStep !== 0 && (
                <Button
                  onClick={handleBack}
                  className={classes.stepperBackBtn}
                  startIcon={<ArrowBackIosIcon />}
                >
                  {t("requestForm.back")}
                </Button>
              )}
              <Stepper nonLinear activeStep={state.activeStep}>
                {steps.map((label, index) => {
                  const labelProps = {};
                  const buttonProps = {};
                  const errorCounts = formErrors[steps[index]] ? Object.keys(formErrors[steps[index]]).length : 0;
                  if(errorCounts !== 0) {
                    labelProps.error = true;
                    buttonProps.optional = (
                      <Typography
                        className={classes.stepperText}
                        variant="body2"
                        color="error"
                        component="span"
                      >
                        {t("requestForm.errors", {count:errorCounts})}
                      </Typography>
                    );
                  }
                  return (
                    <Step key={label} id={`step-${label}`} ref={getStepRef(index)}  tabIndex="-1">
                      <StepButton
                        {...buttonProps} 
                        onClick={()=>handleStep(index)}
                        disabled={props.request === null}
                      >
                        <StepLabel
                          {...labelProps}
                          className={classes.stepperText}
                        >
                          <span className="screen-reader-text">
                            {`Step ${index +1}: `}
                          </span>
                          {t("requestForm."+label+".title")}
                        </StepLabel>
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
              {state.activeStep !== steps.length - 1 && (
                <Button
                  onClick={handleNext}
                  className={classes.stepperNextBtn}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  {t("requestForm.next")}
                </Button>
              )}
            </div>
            <Divider sx={{mt: 3, mb: 3}} />
            {/*<CutCopyPasteAlert /> */}
            <Grid justifyContent="center" container>
              <Grid className={classes.maxWidth640} container>
                <Grid item>
                  {getStepContent(state.activeStep)}
                </Grid>
                <Grid xs={12} item>
                  <Divider sx={{mb: 3}} />
                </Grid>
                <Grid justifyContent="flex-end" container>
                  <Grid item>
                    {state.activeStep !== 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{mr: 2}}
                        onClick={handleBack}
                      >
                        {t("requestForm.back")}
                      </Button>
                    )}
                    {state.activeStep === steps.length - 1 && !props.readOnly && !props.analystEditFlag &&
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                      >
                        {t("requestForm.submitRequest")}
                      </Button>
                    }
                    {state.activeStep !== steps.length - 1 &&
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {t("requestForm.next")}
                      </Button>
                    }
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Container>
        
        <DialogSaveBeforeLeaving
          open={dialogUnsaved.open}
          toggleDialog={handleUnsavedDialogClose}
          handleDontSave={()=>{
            if(dialogUnsaved.isExternal){
              window.location.href=dialogUnsaved.pathname;
            }else{
              history.push(dialogUnsaved.pathname);
            }
          }}
          handleSave={()=>{
            handleUnsavedDialogClose();
            handleSave();
          }}
          snackbar={
            <Snackbar 
              message={snackbarState.message}
              severity={snackbarState.severity}
              open={snackbarState.snackbarLeave}
              handleClose={()=>handleCloseSnackbar("snackbarLeave")}
            />
          }
        />
        <Backdrop className={classes.backdrop} open={backdropOpen}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </main>
      {props.request.status.id===RequestStatusId.Draft && 
      userInfo.role===UserRole.Researcher && 
      userInfo.id===props.request.researcher.account &&
        <FloatingSupportButton 
          handleSupportSubmit={handleSupport} 
          form 
          formSnackbar={
            <Snackbar 
              message={snackbarState.message}
              severity={snackbarState.severity}
              open={snackbarState.snackbarSupport}
              handleClose={()=>handleCloseSnackbar("snackbarSupport")}
            />
          }
          handleCloseSnackbar={()=>handleCloseSnackbar("snackbarSupport")}
        />
      }
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>handleCloseSnackbar('open')}
      />
      <Footer 
        handleSaveBeforeLeave={handleSaveBeforeLeave}
        ref={footerRef}
      />
    </Root>
  );
}

VettingRequest.propTypes={
  request: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  handleRefreshForm: PropTypes.func.isRequired,
};
